import { useEffect, useMemo } from 'react'

import { Filter, QueryOrder } from '@cubejs-client/core'
import { useCubeReportPageFiltersContext } from 'cube'
import {
  MEETINGS_DIMENSIONS,
  MEETINGS_MEASURES,
  MEETINGS_USERS_DIMENSIONS,
} from 'cube/constants'
import moment from 'moment'

import { ReportOrderType } from '@driftt/api-types'
import {
  DDSPaginationState,
  TableStateChange,
  useTableState,
} from '@driftt/dds'
import { CubeQuery, CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import { IQueryConfig, ISortConfig } from 'api/report/query/types'
import { SchedulingEventSummaryMetrics } from 'api/report/types'
import { resolveCubeKPIResults } from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'

import { formatPageFilters } from '../MeetingLineChart/useMeetingCount.hook'

export interface IMeetingsBooked {
  pagination: DDSPaginationState
  handleTableStateChange: (
    newState: TableStateChange<MeetingBookedData>
  ) => void
  partialMetric?: MeetingBookedData[] | null
  fullMetric?: MeetingBookedData[] | null
  totalRowCount: number
  isLoading: boolean
  hasError: boolean
}

interface MeetingBookedData {
  agentEmail: string
  agentId: number
  agentName: string
  createdAt: number
  durationMillis: number
  endUser: any
  endUserId: number
  eventId: string
  meetingEndAt: number
  meetingSource: string
  meetingStartAt: number
  orgId: number
  status: string
}

const toEpochMilli = (date) => {
  const dateObj = new Date(date)
  return dateObj.getTime() - dateObj.getTimezoneOffset() * 60 * 1000
}

const parseId = (id: string) => (id ? parseInt(id) : null)

const formatTableData = (resultSet): MeetingBookedData[] =>
  resultSet?.map((data) => ({
    eventId: data?.[MEETINGS_DIMENSIONS.EVENT_ID],
    agentEmail: data?.[MEETINGS_USERS_DIMENSIONS.AGENT_EMAIL],
    agentId: parseId(data?.[MEETINGS_USERS_DIMENSIONS.AGENT_ID]),
    agentName: data?.[MEETINGS_USERS_DIMENSIONS.AGENT_NAME],
    createdAt: toEpochMilli(data?.[MEETINGS_DIMENSIONS.SCHEDULED_AT]),
    endUserId: parseInt(data?.[MEETINGS_USERS_DIMENSIONS.END_USER_ID]),
    endUser: {
      id: parseId(data?.[MEETINGS_USERS_DIMENSIONS.END_USER_ID]),
      email: data?.[MEETINGS_USERS_DIMENSIONS.END_USER_EMAIL],
      name: data?.[MEETINGS_USERS_DIMENSIONS.END_USER_NAME],
      company: data?.[MEETINGS_USERS_DIMENSIONS.END_USER_COMPANY_NAME],
      title:
        data?.[MEETINGS_USERS_DIMENSIONS.END_USER_COMPANY_EMPLOYMENT_TITLE],
    },
    orgId: parseId(data?.[MEETINGS_USERS_DIMENSIONS.ORG_ID]),
    status: data?.[MEETINGS_DIMENSIONS.STATUS],
    meetingEndAt: toEpochMilli(data?.[MEETINGS_DIMENSIONS.END_AT]),
    meetingStartAt: toEpochMilli(data?.[MEETINGS_DIMENSIONS.START_AT]),
    meetingSource: data?.[MEETINGS_DIMENSIONS.MEETING_SOURCE],
    durationMillis:
      toEpochMilli(data?.[MEETINGS_DIMENSIONS.END_AT]) -
      toEpochMilli(data?.[MEETINGS_DIMENSIONS.START_AT]),
  }))

const mapSortMetric: Record<string, string> = {
  [SchedulingEventSummaryMetrics.CREATED_AT]: MEETINGS_DIMENSIONS.SCHEDULED_AT,
  [SchedulingEventSummaryMetrics.MEETING_START_AT]:
    MEETINGS_DIMENSIONS.START_AT,
  [SchedulingEventSummaryMetrics.AGENT_ID]: MEETINGS_USERS_DIMENSIONS.AGENT_ID,
  [SchedulingEventSummaryMetrics.AGENT_NAME]:
    MEETINGS_USERS_DIMENSIONS.AGENT_EMAIL,
  [SchedulingEventSummaryMetrics.END_USER_NAME]:
    MEETINGS_USERS_DIMENSIONS.END_USER_NAME,
  [SchedulingEventSummaryMetrics.STATUS]: MEETINGS_DIMENSIONS.STATUS,
  [SchedulingEventSummaryMetrics.COMPANY]:
    MEETINGS_USERS_DIMENSIONS.END_USER_COMPANY,
  [SchedulingEventSummaryMetrics.TITLE]:
    MEETINGS_USERS_DIMENSIONS.END_USER_TITLE,
  [SchedulingEventSummaryMetrics.MEETING_SOURCE]:
    MEETINGS_DIMENSIONS.MEETING_SOURCE,
}

const formatSortMetric = (metric: string) => {
  return mapSortMetric[metric]
    ? mapSortMetric[metric]
    : MEETINGS_DIMENSIONS.SCHEDULED_AT
}

const formatSortOrder = (orderType: ReportOrderType) => {
  return orderType.toLowerCase() as QueryOrder
}

export const useMeetingsBooked = (
  sortConfig: ISortConfig,
  queryConfig: IQueryConfig,
  extraFilters: Filter[] = []
) => {
  const { tableState, handleTableStateChange } =
    useTableState<MeetingBookedData>({
      defaultPagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    })

  const timePeriod = useTimePeriod()

  const queryFilters = formatPageFilters(queryConfig.compoundQuery)
  const { pageFilters: cubeFilters } = useCubeReportPageFiltersContext()
  const pageFilters = [...queryFilters, ...cubeFilters, ...extraFilters]

  const meetingsBookedBaseCubeQuery: CubeQuery = {
    name: 'DriftChatSchedulingEvents',
    query: {
      order: {
        [MEETINGS_MEASURES.MEETING_COUNT]: 'desc',
      },
      dimensions: [
        MEETINGS_USERS_DIMENSIONS.ORG_ID,
        MEETINGS_USERS_DIMENSIONS.AGENT_ID,
        MEETINGS_USERS_DIMENSIONS.AGENT_EMAIL,
        MEETINGS_USERS_DIMENSIONS.AGENT_NAME,
        MEETINGS_USERS_DIMENSIONS.END_USER_ID,
        MEETINGS_USERS_DIMENSIONS.END_USER_EMAIL,
        MEETINGS_USERS_DIMENSIONS.END_USER_NAME,
        MEETINGS_USERS_DIMENSIONS.END_USER_COMPANY_NAME,
        MEETINGS_USERS_DIMENSIONS.END_USER_COMPANY_EMPLOYMENT_TITLE,
        MEETINGS_USERS_DIMENSIONS.END_USER_AVATAR_URL,
        MEETINGS_DIMENSIONS.SCHEDULED_AT,
        MEETINGS_DIMENSIONS.EVENT_ID,
        MEETINGS_DIMENSIONS.MEETING_SOURCE,
        MEETINGS_DIMENSIONS.START_AT,
        MEETINGS_DIMENSIONS.END_AT,
        MEETINGS_DIMENSIONS.STATUS,
      ],
    },
    queryName: 'DriftChatSchedulingEvents',
  }

  const timeDimensions: Array<any> = [
    {
      dimension: MEETINGS_DIMENSIONS.SCHEDULED_AT,
      dateRange: [
        moment(timePeriod.startDate).format('YYYY-MM-DD'),
        moment(timePeriod.endDate).format('YYYY-MM-DD'),
      ],
    },
  ]

  const paginatedMetricQuery = new CubeQueryBuilder({
    query: meetingsBookedBaseCubeQuery,
  })
    .withName(
      `MeetingsBooked-${tableState.pagination.pageIndex}-${tableState.pagination.pageSize}`
    )
    .withTimeDimensions(timeDimensions)
    .withLimit(tableState.pagination.pageSize)
    .withOffset(
      tableState.pagination.pageIndex * tableState.pagination.pageSize
    )
    .withFilters(pageFilters)
    .withOrder([
      [formatSortMetric(sortConfig.metric), formatSortOrder(sortConfig.order)],
    ])
    .build()

  const {
    data: paginatedResultSet,
    refetch,
    isLoading: isLoadingData,
    isError: isErrorData,
  } = useCubeQuery({
    query: paginatedMetricQuery,
  })

  const paginatedData = useMemo(
    () => formatTableData(paginatedResultSet?.tablePivot()),
    [paginatedResultSet]
  )

  const partialMetric = useMemo(() => paginatedData ?? null, [paginatedData])

  const countQuery = new CubeQueryBuilder({})
    .withTimeDimensions(timeDimensions)
    .withMeasures([MEETINGS_MEASURES.MEETING_COUNT])
    .withFilters(pageFilters)
    .build()

  const {
    data: countResultSet,
    isLoading: isLoadingCount,
    isError: isErrorCount,
  } = useCubeQuery({
    query: countQuery,
  })

  const totalRowCount = useMemo(
    () =>
      resolveCubeKPIResults(countResultSet, MEETINGS_MEASURES.MEETING_COUNT),
    [countResultSet]
  )

  const fullMetricQuery = new CubeQueryBuilder({
    query: meetingsBookedBaseCubeQuery,
  })
    .withTimeDimensions(timeDimensions)
    .withFilters(pageFilters)
    .build()

  const { data: fullResultSet } = useCubeQuery({
    query: fullMetricQuery,
  })

  const fullMetric = useMemo(
    () => formatTableData(fullResultSet?.tablePivot()) || [],
    [fullResultSet]
  )

  const isLoading = useMemo(
    () => isLoadingData || isLoadingCount,
    [isLoadingData, isLoadingCount]
  )

  const isError = useMemo(
    () => isErrorData || isErrorCount,
    [isErrorCount, isErrorData]
  )

  useEffect(() => {
    refetch()
  }, [sortConfig, refetch])

  return useMemo(
    () => ({
      isLoading,
      isError,
      partialMetric,
      totalRowCount,
      fullMetric,
      pagination: tableState.pagination,
      handleTableStateChange,
    }),
    [
      isLoading,
      isError,
      partialMetric,
      fullMetric,
      totalRowCount,
      tableState.pagination,
      handleTableStateChange,
    ]
  )
}

export default useMeetingsBooked
